import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import SensorsOffRoundedIcon from '@mui/icons-material/SensorsOffRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type StatusSwitchProps = {
  checked?: boolean
  disabled?: boolean
  error?: boolean
  onChange?: (checked: boolean) => void
}

export function StatusSwitch(props: StatusSwitchProps) {
  const { checked: checkedProp = false, disabled = false, error = false, onChange } = props
  const { t } = useTranslation()

  const [checked, setChecked] = useState<boolean>(checkedProp)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setChecked(checkedProp)
    setLoading(false)
  }, [checkedProp])

  // if there is error, switch back to previous state
  useEffect(() => {
    if (!!error) setChecked((prevChecked) => !prevChecked)
    setLoading(false)
  }, [error])

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!disabled && !loading) {
      setChecked(checked)
      setLoading(true)
      onChange?.(checked)
    }
  }

  const icon = (
    <Box component="span" sx={{ backgroundColor: checked ? colors.green700 : colors.gray700, width: '32px', height: '32px', borderRadius: '50%' }}>
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        {loading ? <CircularProgress color="inherit" size={16} thickness={5} /> : checked ? <SensorsRoundedIcon /> : <SensorsOffRoundedIcon />}
      </Stack>
    </Box>
  )

  return (
    <Tooltip title={t(`common:model.staff.waitingRoom.status.${checkedProp ? 'available' : 'unavailable'}.label`)}>
      <Switch
        checked={checked}
        checkedIcon={icon}
        icon={icon}
        onChange={handleChange}
        sx={{
          width: '62px',
          height: '34px',
          padding: '7px',
          '& .MuiSwitch-switchBase': {
            margin: '1px',
            padding: 0,
            transform: 'translateX(6px)',
            '&.Mui-checked': {
              color: '#fff',
              transform: 'translateX(22px)',
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: colors.green400,
              },
            },
          },
          '& .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: colors.gray400,
            borderRadius: 20 / 2,
          },
        }}
      />
    </Tooltip>
  )
}
